import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  InputAdornment,
  Avatar,
  Paper,
  useMediaQuery,
  useTheme
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LockIcon from '@mui/icons-material/Lock';
import BadgeIcon from '@mui/icons-material/Badge';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useNavigate } from 'react-router-dom';
import Navbar from '../Navbar';
import { is } from 'date-fns/locale';

const PRIMARY_COLOR = '#616161';

const ResellerAccountForm = () => {
  const [formData, setFormData] = useState({
    resellerId: '',
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    profileImage: null,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [preview, setPreview] = useState(null);
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // ✅ check screen size

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'profileImage') {
      const file = files[0];
      setFormData((prev) => ({ ...prev, profileImage: file }));
      setPreview(URL.createObjectURL(file));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formPayload = new FormData();
    Object.keys(formData).forEach((key) => {
      if (formData[key]) formPayload.append(key, formData[key]);
    });

    try {
      const response = await fetch('https://reviewhub-lc.onrender.com/api/resellers/create', {
        method: 'POST',
        body: formPayload,
      });

      const data = await response.json();
      if (!response.ok) {
        alert(data.error || 'Something went wrong');
      } else {
        alert('Reseller account created successfully!');
      }
    } catch (err) {
      console.error(err);
      alert('Server error');
    }
  };

  return (
    
    <>
      <Navbar />
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, px: isMobile ? 2 : 0 ,width:isMobile ? '100%' : '650px'}}>
        <Paper
          elevation={6}
          sx={{
            mt: isMobile ? 4 : 8,
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              backgroundColor: PRIMARY_COLOR,
              py: 3,
              textAlign: 'center',
            }}
          >
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold" color="white">
              Create Reseller Account
            </Typography>
          </Box>

          {/* Form */}
          <Box component="form" onSubmit={handleSubmit} sx={{ p: isMobile ? 2 : 4, backgroundColor: '#f9f9f9' }}>
            <Grid container spacing={3}>

              {/* Row 1 */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Reseller ID"
                  name="resellerId"
                  value={formData.resellerId}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BadgeIcon sx={{ color: PRIMARY_COLOR }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: PRIMARY_COLOR }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Row 2 */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: PRIMARY_COLOR }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon sx={{ color: PRIMARY_COLOR }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Row 3 */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: PRIMARY_COLOR }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type={showConfirm ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  error={
                    formData.confirmPassword &&
                    formData.confirmPassword !== formData.password
                  }
                  helperText={
                    formData.confirmPassword &&
                    formData.confirmPassword !== formData.password
                      ? 'Passwords do not match'
                      : ''
                  }
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: PRIMARY_COLOR }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* File Upload */}
              <Grid item xs={12} textAlign="center">
                <input
                  accept="image/*"
                  type="file"
                  name="profileImage"
                  id="profileImage"
                  onChange={handleChange}
                  style={{ display: 'none' }}
                />
                <label htmlFor="profileImage">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadFileIcon />}
                    sx={{
                      color: PRIMARY_COLOR,
                      borderColor: PRIMARY_COLOR,
                      borderRadius: 2,
                      ':hover': {
                        backgroundColor: PRIMARY_COLOR,
                        color: '#fff',
                        borderColor: PRIMARY_COLOR,
                      },
                    }}
                  >
                    Upload Profile Image
                  </Button>
                </label>
                {preview && (
                  <Box mt={2}>
                    <Avatar
                      src={preview}
                      sx={{
                        width: 90,
                        height: 90,
                        margin: '10px auto',
                        border: `2px solid ${PRIMARY_COLOR}`,
                      }}
                    />
                  </Box>
                )}
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{
                    py: 1.5,
                    fontWeight: 'bold',
                    backgroundColor: PRIMARY_COLOR,
                    borderRadius: 2,
                    ':hover': {
                      backgroundColor: PRIMARY_COLOR,
                    },
                  }}
                >
                  Create Account
                </Button>
              </Grid>

              {/* Login Link */}
              <Grid item xs={12} textAlign="center" mt={2}>
                <Typography variant="body2">
                  Already have an account?{' '}
                  <span
                    style={{
                      color: PRIMARY_COLOR,
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                    onClick={() => navigate('/reseller-login')}
                  >
                    Login
                  </span>
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default ResellerAccountForm;
